import type { MouseEventHandler, ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@shared/lib/utils";

type GlowButtonProps = {
  children: ReactNode;
  /** Internal route — renders a react-router Link. */
  to?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  variant?: "primary" | "ghost";
  size?: "md" | "lg";
  /** 타이포 변형 — 기본 mono 11px uppercase, "cta"는 Courtyard CTA(14/600 Inter). */
  textStyle?: "mono" | "cta";
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  /** 키보드 단축키 힌트 (예: "F", "Shift+A") — 스크린 리더에 노출. */
  "aria-keyshortcuts"?: string;
};

const SIZE_STYLES = {
  md: "min-h-11 px-5 py-2.5",
  lg: "min-h-11 px-7 py-3",
} as const;

const TEXT_STYLES = {
  mono: { md: "font-mono font-medium uppercase tracking-[0.12em] text-[11px]", lg: "font-mono font-medium uppercase tracking-[0.12em] text-xs" },
  cta: { md: "font-sans text-sm font-semibold normal-case tracking-normal", lg: "font-sans text-sm font-semibold normal-case tracking-normal" },
} as const;

// Matches the ring PackLineup.tsx's pack tiles use — every purchase CTA
// (Open pack, Open ×10, Sign in, RevealOverlay's Continue/Reveal next) is a
// GlowButton, so a keyboard user tabbing through the buy flow needs the same
// visible accent ring the rest of the app already gives its interactive elements.
const FOCUS_RING =
  "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70";

const VARIANT_STYLES = {
  primary: "ty-cta",
  ghost:
    "border border-line bg-transparent text-ink-dim transition-colors duration-200 hover:border-accent/60 hover:text-ink",
} as const;

/** CTA — primary is the quiet gold fill with gold text (10px radius); `ghost` is a hairline button. */
export function GlowButton({
  children,
  to,
  onClick,
  variant = "primary",
  size = "md",
  textStyle = "mono",
  disabled = false,
  className,
  "aria-label": ariaLabel,
  "aria-keyshortcuts": ariaKeyShortcuts,
}: GlowButtonProps) {
  const classes = cn(
    "inline-flex w-fit items-center justify-center gap-2 rounded-(--ty-radius-btn)",
    TEXT_STYLES[textStyle][size],
    SIZE_STYLES[size],
    VARIANT_STYLES[variant],
    FOCUS_RING,
    disabled && "pointer-events-none opacity-50",
    className,
  );

  if (to) {
    // A disabled link must not navigate or take focus — render an inert span
    // with the same visual classes instead of a Link.
    if (disabled) {
      return (
        <span aria-disabled="true" aria-label={ariaLabel} className={classes}>
          {children}
        </span>
      );
    }
    return (
      <Link to={to} onClick={onClick} aria-label={ariaLabel} className={classes}>
        {children}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-keyshortcuts={ariaKeyShortcuts}
      className={classes}
    >
      {children}
    </button>
  );
}
